import { tv, type VariantProps } from "tailwind-variants"
import {
  type TextColorVariantProps,
  textColorVariants,
} from "../../styles/utils/color"
import { classNames } from "../../utils/classNames"
import { SkeletonLine, type SkeletonProps } from "../Skeleton"
import { Slot, type SlotProps } from "../Slot"

export type TextBodyProps = SlotProps<"span"> &
  VariantProps<typeof textBodyVariants> &
  TextColorVariantProps

export const textBodyVariants = tv({
  variants: {
    size: {
      xs: "text-xs",
      sm: "text-sm",
      md: "text-md",
    },
    weight: {
      regular: "font-normal",
      semibold: "font-medium",
    },
  },
})

/**
 * Body provides the most number of sizes and weights options.
 * Body can be used for paragraph text to sm legal text or comyright.
 * This will be the most common type of text through out OpenSea.
 * Medium body can be used for paragraphs, stand-alone links and form labels.
 * Small body can be used for supplemental text, sublabels, card text and stats.
 * Tiny body can be use for informational, stats, captions, and legal text.
 */
function TextBody({
  asChild,
  className,
  weight,
  color,
  size = "sm",
  ...rest
}: TextBodyProps) {
  const Comp = asChild ? Slot : "span"

  return (
    <Comp
      className={classNames(
        "leading-normal",
        textBodyVariants({
          weight,
          size,
        }),
        textColorVariants({ color }),
        className,
      )}
      {...rest}
    />
  )
}

export const textBodySkeletonVariants = tv({
  variants: {
    size: {
      xs: "my-[3px] h-3",
      sm: "my-[3.5px] h-3.5",
      md: "my-1 h-4",
    },
  },
})

const TextBodySkeleton = ({
  className,
  size = "sm",
  ...rest
}: SkeletonProps & Pick<TextBodyProps, "size" | "className">) => {
  return (
    <SkeletonLine
      className={classNames(textBodySkeletonVariants({ size }), className)}
      {...rest}
    />
  )
}

export { TextBody, TextBodySkeleton }
